import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Rocket, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const HeroSection = () => {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      aria-label="Hero: connect founders and investors">
      
      {/* Abstract background */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/8 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/8 rounded-full blur-3xl" />
      </div>

      {/* Animated floating cards */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {[
        { x: "10%", y: "25%", delay: 0, label: "Series A · $2M", sub: "FinTech · London" },
        { x: "72%", y: "18%", delay: 0.4, label: "Pre-Seed · $500K", sub: "HealthTech · NYC" },
        { x: "80%", y: "60%", delay: 0.8, label: "Seed · $1.2M", sub: "CleanTech · Berlin" },
        { x: "5%", y: "65%", delay: 1.2, label: "Bridge · $800K", sub: "EdTech · Singapore" }].
        map((card, i) =>
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 0.18, y: [0, -12, 0] }}
          transition={{
            opacity: { delay: card.delay + 0.5, duration: 0.8 },
            y: { delay: card.delay, duration: 4 + i * 0.5, repeat: Infinity, ease: "easeInOut" }
          }}
          style={{ left: card.x, top: card.y }}
          className="absolute glass rounded-xl px-4 py-3 min-w-[150px]">
          
            <p className="text-xs font-semibold text-primary">{card.label}</p>
            <p className="text-xs text-muted-foreground">{card.sub}</p>
          </motion.div>
        )}

        {/* Animated connection lines */}
        <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
          <motion.line
            x1="15%" y1="30%" x2="50%" y2="50%"
            stroke="hsl(174 62% 47%)" strokeWidth="1" strokeDasharray="6 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1, repeat: Infinity, repeatType: "loop", repeatDelay: 3 }} />
          
          <motion.line
            x1="75%" y1="22%" x2="50%" y2="50%"
            stroke="hsl(200 60% 45%)" strokeWidth="1" strokeDasharray="6 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1.5, repeat: Infinity, repeatType: "loop", repeatDelay: 3 }} />
          
          <motion.line
            x1="82%" y1="62%" x2="50%" y2="50%"
            stroke="hsl(174 62% 47%)" strokeWidth="1" strokeDasharray="6 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 2, repeat: Infinity, repeatType: "loop", repeatDelay: 3 }} />
          
          <motion.line
            x1="8%" y1="68%" x2="50%" y2="50%"
            stroke="hsl(200 60% 45%)" strokeWidth="1" strokeDasharray="6 4"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 2.5, repeat: Infinity, repeatType: "loop", repeatDelay: 3 }} />
          
        </svg>

        {/* Central pulsing node */}
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary"
          animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0.15, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />
        
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 mb-8">
            
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
            <span className="text-sm font-medium text-primary">Now in Early Access</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold leading-tight mb-6">
            
            Where Founders Meet{" "}
            <span className="text-gradient bg-[#2e8ab8]/[0.49]">The Right Investors</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">AdV3nture is a structured discovery platform that connects startup founders with thesis-aligned investors by stage, sector, and geography. No cold emails. Just relevant connections.


          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4">
            
            <Button asChild size="lg" className="glow-primary text-base px-8 py-6 gap-2">
              <Link to="/for-founders">
                <Rocket className="w-4 h-4" aria-hidden="true" /> I am a Founder
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="text-base px-8 py-6 gap-2 bg-secondary/20 border border-secondary/50 text-secondary hover:bg-secondary/30"
              style={{ boxShadow: "0 0 40px hsl(200 60% 45% / 0.25)" }}>
              
              <Link to="/for-investors">
                <TrendingUp className="w-4 h-4" aria-hidden="true" /> I'm an Investor
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default HeroSection;